import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { quatEuler, v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import { MQuerleistenwerte } from "../../metamodell";
import { Flügel } from "./Flügel";
import { Box3 } from "three";
import { ghostPool } from "./Arrangement";
import { ModelGhost } from "oc-jaco3d/arrangement/ModelGhost";

const epsilon = 0.0001;

export class Querleisten extends Jaco {
  static CLASS_NAME = "Querleisten";

  meta: MQuerleistenwerte = null;
  modelle: ModelGhost[] = [];
  box = new Box3();
  size = v3();
  flügel: Flügel;

  constructor(parent, flügel) {
    super(parent);
    this.flügel = flügel;
  }

  traverse(iterator: (jaco: Jaco) => void) {
    super.traverse(iterator);
    if (this.modelle) this.modelle.forEach((o) => o.traverse(iterator));
  }

  getBox() {
    return this.box.clone();
  }

  update3d() {
    super.update3d();
    this.modelle = ghostPool(this).clearInstances(this.modelle);
    const bezugsBox = this.flügel.rahmenInnenBox;
    this.position.copy(bezugsBox.min);
    this.size.copy(bezugsBox.getSize(v3()));
    const zAbstand = this.flügel.element.seiteGewechselt
      ? this.meta.Z_Abstand
      : -this.meta.Z_Abstand;
    this.position.z += this.size.z / 2 + zAbstand;
    Maths3.boxFromGridMinAndSize(v3(0, 0, 0.5), this.size, this.box);
    this.updateQuerleistenUndWinkelverstrebungen();
    this.modelle.forEach((m) => m.update3d());
  }

  addModel(meta: any, sizeX: number, gehrung45: boolean) {
    // Konvention für Gridposition der Modelle: 0.5,0.5,0.5
    const mmr = meta.ModelMaterialRelation?.[0];
    const model = ghostPool(this).getForModelMaterialRelation(this, mmr, {
      size: v3(sizeX, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
      fixGridPosition: v3(0.5, 0.5, 0.5),
      gehrung45,
    });
    this.modelle.push(model);
    return model;
  }

  updateQuerleistenUndWinkelverstrebungen() {
    const {
      Querleistenobjekt,
      Winkelverstrebung,
      RahmenabstandOben,
      RahmenabstandUnten,
      RahmenabstandUnten2,
      RahmenabstandUnten3,
      Mindesthöhe_3,
      Mindesthöhe_4,
    } = this.meta;

    const addQuerleiste = (y) => {
      const model = this.addModel(Querleistenobjekt, this.size.x, false);
      const rotationX = this.flügel.element.seiteGewechselt ? 0 : 180;
      model.quaternion.copy(quatEuler(rotationX, 0, 0));
      model.position.setX(this.size.x / 2).setY(y);
      (model as any).isHorizontal = true;
    };

    const modelSize = APP.modelsHelper
      .getBoundingBox(Querleistenobjekt.ModelMaterialRelation?.[0])
      .getSize(v3());

    const flügelSize = this.size;

    const maxY = this.box.max.y - modelSize.y * 0.5;
    const minY = this.box.min.y + modelSize.y * 0.5;

    addQuerleiste(maxY - RahmenabstandOben); // obere Leiste
    if (Mindesthöhe_4 > epsilon && flügelSize.y > Mindesthöhe_4 - epsilon) {
      const topY =
        RahmenabstandUnten2 > epsilon ? maxY - RahmenabstandUnten2 : minY + ((maxY - minY) * 2) / 3;
      const botY =
        RahmenabstandUnten3 > epsilon ? minY + RahmenabstandUnten3 : minY + ((maxY - minY) * 1) / 3;
      addQuerleiste(topY); // mittlere obere Leiste
      addQuerleiste(botY); // mittlere untere Leiste
    } else if (Mindesthöhe_3 > epsilon && flügelSize.y > Mindesthöhe_3 - epsilon) {
      const midY =
        RahmenabstandUnten2 > epsilon
          ? maxY - RahmenabstandUnten2
          : RahmenabstandUnten3 > epsilon
            ? maxY + RahmenabstandUnten3
            : (minY + maxY) * 0.5;
      addQuerleiste(midY); // mittlere Leiste
    }
    addQuerleiste(minY + RahmenabstandUnten); // untere Leiste

    const innenBox = this.box.clone();
    innenBox.min.y += RahmenabstandUnten;
    innenBox.max.y -= RahmenabstandOben;

    const addWinkelverstrebung = (sizeX, rotationZ, partPos, boxGridPos, gehrung45) => {
      const part = this.addModel(Querleistenobjekt, sizeX, gehrung45);
      part.quaternion.copy(quatEuler(0, 0, rotationZ));
      const bgp = innenBox.grid(boxGridPos);
      part.position.copy(bgp).sub(partPos);
      return part;
    };

    if (Winkelverstrebung > epsilon) {
      const sqrt2 = Math.sqrt(2);
      const wvLength = sqrt2 * Winkelverstrebung;
      const notUnderstoodDelta = -0.002;
      const wvy = (sqrt2 * modelSize.y) / 2 + Winkelverstrebung / 2 + notUnderstoodDelta;

      // right-top corner
      addWinkelverstrebung(wvLength, 270 + 45, v3(wvy, wvy, 0), v3(1, 1, 0.5), true);
      // left-top corner
      addWinkelverstrebung(wvLength, 0 + 45, v3(-wvy, wvy, 0), v3(0, 1, 0.5), true);
      // left-bottom corner
      addWinkelverstrebung(wvLength, 90 + 45, v3(-wvy, -wvy, 0), v3(0, 0, 0.5), true);
      // right-bottom corner
      addWinkelverstrebung(wvLength, 180 + 45, v3(wvy, -wvy, 0), v3(1, 0, 0.5), true);
    }
  }
}
